import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { RestaurantProvider, useRestaurant } from "@/context/RestaurantContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ClientDashboard from "@/pages/client/Dashboard";
import ClientHistory from "@/pages/client/History";
import ClientStats from "@/pages/client/Stats";
import ClientProfile from "@/pages/client/Profile";
import RestaurantSelector from "@/pages/client/RestaurantSelector";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminClients from "@/pages/admin/Clients";
import AdminProducts from "@/pages/admin/Products";
import AdminOrders from "@/pages/admin/Orders";
import AdminClientProducts from "@/pages/admin/ClientProducts";
import AdminProfile from "@/pages/admin/Profile";
import AdminBranchPrices from "@/pages/admin/BranchPrices";
import AdminSpecialPrices from "@/pages/admin/SpecialPrices";
import AdminPricePreview from "@/pages/admin/PricePreview";
import AdminA3Sync from "@/pages/admin/A3Sync";
import UpdateBanner from "@/components/UpdateBanner";
import useAppVersion from "@/hooks/useAppVersion";
import "@/App.css";

// Error Boundary para evitar pantallas blancas
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8">
            <h2 className="text-xl font-bold text-red-600 mb-4">Algo salió mal</h2>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ClientRoute = ({ children }) => {
  return (
    <RestaurantProvider>
      <ClientRouteInner>{children}</ClientRouteInner>
    </RestaurantProvider>
  );
};

const ClientRouteInner = ({ children }) => {
  const { selected, restaurants, loading } = useRestaurant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  // Show selector if 2+ restaurants and none selected
  if (!selected && restaurants.length > 1) {
    return <RestaurantSelector />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={<Navigate to="/login" replace />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ClientRoute>
              <ClientDashboard />
            </ClientRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <ClientRoute>
              <ClientHistory />
            </ClientRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <ClientRoute>
              <ClientStats />
            </ClientRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ClientRoute>
              <ClientProfile />
            </ClientRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute adminOnly>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute adminOnly>
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/restaurants/:restaurantId/products"
        element={
          <ProtectedRoute adminOnly>
            <AdminClientProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute adminOnly>
            <AdminProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prices/branch"
        element={
          <ProtectedRoute adminOnly>
            <AdminBranchPrices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prices/special"
        element={
          <ProtectedRoute adminOnly>
            <AdminSpecialPrices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/prices/preview"
        element={
          <ProtectedRoute adminOnly>
            <AdminPricePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sync"
        element={
          <ProtectedRoute adminOnly>
            <AdminA3Sync />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useAppVersion();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
            <UpdateBanner />
            <Toaster position="top-right" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
